import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { RouterModule } from '@angular/router';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';
import { AnalyticsService } from '../../../../shared/services/analytics.service';

export interface PricingPlan {
  id: string;
  title: string;
  price: string;
  priceNote: string;
  billingNote?: string;
  description: string;
  features: string[];
  exclusiveEyebrow?: string;
  exclusiveHeading?: string;
  exclusiveBody?: string;
  buttonLabel: string;
  buttonLink: string;
  queryParams?: Record<string, string>;
  secondaryLink: string;
  secondaryQueryParams?: Record<string, string>;
  highlighted: boolean;
  contactOnly: boolean;
}

@Component({
  selector: 'app-pricing-table',
  imports: [
    MatButtonModule,
    MatCardModule,
    CommonModule,
    MatDividerModule,
    RouterModule
  ],
  templateUrl: './pricing-table.component.html',
  styleUrl: './pricing-table.component.scss',
  animations: [
    trigger('cascadeFadeIn', [
      transition(':enter', [
        query('.pricing-card', [
          style({ opacity: 0, transform: 'translateY(40px)' }),
          stagger(120, [
            animate('700ms cubic-bezier(0.23, 1, 0.32, 1)', style({ opacity: 1, transform: 'none' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class PricingTableComponent {
  private readonly analytics = inject(AnalyticsService);

  pricingPlans: PricingPlan[] = [
    {
      id: 'society',
      title: 'Society',
      price: '$299',
      priceNote: '/mo · 500 active members included',
      billingNote: 'Billed monthly · $1/mo per extra active member',
      description: 'The complete MemberTrace platform for most independent organizations.',
      features: [
        'Membership management with automated renewal reminders',
        'Events, payments, and communications',
        'Custom domain and full branding',
        'Custom fields, forms, and directories',
        'Saved reports and board dashboards',
        'Multiple admin seats',
        'Email, chat, and phone support',
      ],
      buttonLabel: 'Get started',
      buttonLink: '/register-organization',
      queryParams: { plan: 'society' },
      secondaryLink: 'Book a demo',
      secondaryQueryParams: { inquiryType: 'demo', plan: 'society' },
      highlighted: true,
      contactOnly: false,
    },
    {
      id: 'growth',
      title: 'Growth',
      price: '$599',
      priceNote: '/mo · 1,500 active members included',
      billingNote: 'Billed monthly · $1/mo per extra active member',
      description: 'For larger or more complex organizations that need advanced control, custom workflows, and integrations.',
      features: [
        'Everything in Society, plus:',
        'Advanced permissions',
        'Custom workflow (automation) builder',
        'API, integrations, and accounting exports',
        'Advanced reporting',
        'Dedicated onboarding specialist',
      ],
      buttonLabel: 'Get started',
      buttonLink: '/register-organization',
      queryParams: { plan: 'growth' },
      secondaryLink: 'Book a demo',
      secondaryQueryParams: { inquiryType: 'demo', plan: 'growth' },
      highlighted: false,
      contactOnly: false,
    },
    {
      id: 'operator',
      title: 'Operator',
      price: 'From $1,500',
      priceNote: '/mo · up to 3 orgs & 1,500 members',
      billingNote: 'Custom terms · talk to sales',
      description: 'For teams that operate multiple member organizations from one workspace.',
      features: [
        'Operator Workspace for multiple organizations',
        'Everything in Growth',
        'Portfolio dashboard and shared staff access',
        'Separate branding per organization',
        'Up to 3 organizations and 1,500 members included',
      ],
      exclusiveEyebrow: 'Exclusive to Operator',
      exclusiveHeading: 'Operator Workspace',
      exclusiveBody: 'Run every organization you support from one workspace.',
      buttonLabel: 'Talk to sales',
      buttonLink: '/contact',
      queryParams: { inquiryType: 'pricing', plan: 'operator' },
      secondaryLink: 'Book a demo',
      secondaryQueryParams: { inquiryType: 'demo', plan: 'operator' },
      highlighted: false,
      contactOnly: true,
    },
  ];

  onPrimaryCta(plan: PricingPlan): void {
    this.analytics.trackCtaClick(this.primaryCtaId(plan));
  }

  primaryCtaId(plan: PricingPlan): string {
    return plan.id === 'operator' ? 'pricing-operator-click' : `pricing-get-started-${plan.id}`;
  }
}
